import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, Upload, History, LogOut, BookOpen, CheckCircle, Crown, Zap, Target, ShoppingCart, Lock, RefreshCw, FolderOpen, BookMarked, Clock, Database } from "lucide-react";
import ProfileDropdown from "@/components/ProfileDropdown";

const API = window.location.origin;

export default function Dashboard({ user, onLogout, onUserUpdate, subjectSelection, onClearSubject, onMediumChange }) {
  const navigate = useNavigate();
  const [appSettings, setAppSettings] = useState(null);
  const [showMediumPicker, setShowMediumPicker] = useState(false);
  
  // Fetch app settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API}/api/settings/settings?admin_key=admin@emrs2025`);
        if (res.ok) {
          const data = await res.json();
          setAppSettings(data);
        }
      } catch (e) {
        console.log('Could not fetch settings');
      }
    };
    fetchSettings();
  }, []);
  
  // Get MCQ limit based on user plan
  const getMcqLimit = () => {
    if (!appSettings) return 20; // default
    if (user.plan === 'free' || !user.plan) return appSettings.free_mcq_per_topic || 5;
    if (user.plan === 'unlimited') return appSettings.unlimited_mcq_per_topic || 50;
    return appSettings.paid_mcq_per_topic || 20;
  };
  
  // Get descriptive limit
  const getDescriptiveLimit = () => {
    if (!appSettings) return user.questions_limit || 60;
    if (user.plan === 'free' || !user.plan) return appSettings.free_descriptive_limit || 1;
    if (user.plan === 'unlimited' || user.questions_limit === -1) return -1;
    return appSettings.paid_descriptive_limit || 60;
  };
  
  const effectiveDescLimit = getDescriptiveLimit();
  const effectiveMcqLimit = getMcqLimit();
  
  const isFreeUser = !user.plan || user.plan === 'free';
  const hasQuotaLeft = effectiveDescLimit === -1 || (effectiveDescLimit - user.questions_used) > 0;

  const handleChangeSubject = () => {
    onClearSubject();
    navigate('/category');
  };

  const handleMediumChange = (lang) => {
    if (onMediumChange) onMediumChange(lang);
    setShowMediumPicker(false);
  };

  const getPlanBadge = () => {
    if (user.plan === 'unlimited') return <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white"><Crown className="w-3 h-3 mr-1" />Unlimited</Badge>;
    if (user.plan === 'premium') return <Badge className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white"><Crown className="w-3 h-3 mr-1" />Premium</Badge>;
    if (user.plan === 'standard') return <Badge className="bg-gradient-to-r from-purple-400 to-pink-500 text-white"><Zap className="w-3 h-3 mr-1" />Standard</Badge>;
    if (user.plan === 'basic') return <Badge className="bg-gradient-to-r from-blue-400 to-cyan-500 text-white">Basic</Badge>;
    if (user.plan === 'tier2_pro') return <Badge className="bg-gradient-to-r from-green-400 to-emerald-500 text-white"><Zap className="w-3 h-3 mr-1" />Premium</Badge>;
    if (user.plan === 'lokesh_notes') return <Badge className="bg-gradient-to-r from-green-400 to-emerald-500 text-white"><BookOpen className="w-3 h-3 mr-1" />Premium</Badge>;
    // Any other non-null, non-free plan → paid
    if (user.plan && user.plan !== 'free') return <Badge className="bg-gradient-to-r from-green-500 to-teal-500 text-white"><CheckCircle className="w-3 h-3 mr-1" />Paid</Badge>;
    return <Badge variant="secondary">Free</Badge>;
  };

  const getQuestionsRemaining = () => {
    const limit = effectiveDescLimit;
    if (limit === -1) return "Unlimited";
    const remaining = limit - user.questions_used;
    return `${remaining > 0 ? remaining : 0} / ${limit}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <GraduationCap className="w-8 h-8 text-blue-600" />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Descriptive Test
              </span>
            </div>
            <div className="flex items-center gap-3">
              {getPlanBadge()}
              <ProfileDropdown user={user} onUserUpdate={onUserUpdate} onLogout={onLogout} />
            </div>
          </div>
        </div>
      </nav>

      {/* Subject Selection Banner */}
      {subjectSelection && (
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className="bg-white/20 text-white border-white/30">
                  {subjectSelection.categoryLabel}
                </Badge>
                <span className="text-lg font-semibold">{subjectSelection.subjectLabel}</span>
                {subjectSelection.language && (
                  <Badge className={`border-white/30 text-white text-xs ${subjectSelection.language === 'hindi' ? 'bg-orange-500/70' : 'bg-blue-400/70'}`}>
                    {subjectSelection.language === 'hindi' ? 'हिंदी Medium' : 'English Medium'}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Medium Toggle */}
                {showMediumPicker ? (
                  <div className="flex items-center gap-2 bg-white/10 rounded-lg px-2 py-1">
                    <span className="text-xs text-white/70 mr-1">Medium:</span>
                    <button
                      onClick={() => handleMediumChange('english')}
                      className={`px-3 py-1 rounded text-xs font-semibold transition-all ${subjectSelection.language === 'english' ? 'bg-white text-blue-700' : 'text-white/80 hover:bg-white/20'}`}
                      data-testid="medium-switch-english"
                    >
                      English
                    </button>
                    <button
                      onClick={() => handleMediumChange('hindi')}
                      className={`px-3 py-1 rounded text-xs font-semibold transition-all ${subjectSelection.language === 'hindi' ? 'bg-orange-400 text-white' : 'text-white/80 hover:bg-white/20'}`}
                      data-testid="medium-switch-hindi"
                    >
                      हिंदी
                    </button>
                    <button onClick={() => setShowMediumPicker(false)} className="text-white/60 hover:text-white ml-1 text-xs">✕</button>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowMediumPicker(true)}
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20 text-xs"
                    data-testid="change-medium-btn"
                  >
                    Change Medium
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleChangeSubject}
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="change-subject-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Change
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            EMRS KVS NVS
            <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Descriptive Paper Practice
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Practice EMRS descriptive syllabus, KVS NVS descriptive paper questions. Upload handwritten answers and get instant expert feedback.
          </p>
        </div>

        {/* Paywall CTA for free users */}
        {(isFreeUser && !hasQuotaLeft) && (
          <Card className="mb-10 border-2 border-orange-300 bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 shadow-xl" data-testid="paywall-cta">
            <CardContent className="py-8 px-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
                Get Full Access to Practice Questions
              </h2>
              <p className="text-gray-600 mb-2 max-w-xl mx-auto text-base">
                To unlock descriptive & objective practice questions, buy
              </p>
              <p className="text-xl font-bold text-orange-700 mb-6">
                TIER-2 PRO PLAN @ ₹499
              </p>
              <Link to="/pro" data-testid="buy-course-btn">
                <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all">
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Upgrade Now
                </Button>
              </Link>
              <p className="text-sm text-gray-500 mt-4">
                Unlimited Notes + MCQ + {effectiveDescLimit === -1 ? 'Unlimited' : effectiveDescLimit} Descriptive
              </p>
            </CardContent>
          </Card>
        )}

        <div className="grid md:grid-cols-4 gap-6 mb-6">
          {/* Full Mock Test - MOVED TO FIRST POSITION */}
          <Link to="/mock-test" data-testid="mock-test-link">
            <Card className="h-full hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer border-2 border-transparent hover:border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50">
              <CardHeader>
                <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <Zap className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl">Full Mock Test</CardTitle>
                <CardDescription className="text-sm">Complete mock test with objective & descriptive questions</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600" />70 Questions (60 + 10)</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600" />CBSE Pattern</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600" />Auto Evaluation</li>
                </ul>
                <div className="mt-4 pt-4 border-t"><p className="text-xs text-gray-500">Complete test experience with timer</p></div>
              </CardContent>
            </Card>
          </Link>

          {(isFreeUser && !hasQuotaLeft) ? (
            <div className="relative" data-testid="evaluate-link-locked">
              <Card className="h-full opacity-50 pointer-events-none border-2 border-gray-200 bg-gradient-to-br from-blue-50 to-indigo-50">
                <CardHeader>
                  <div className="w-14 h-14 bg-gray-400 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                    <Lock className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-xl">Practice Descriptive</CardTitle>
                  <CardDescription className="text-sm">
                    Upload handwritten answer for EMRS, KVS, NVS descriptive questions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Access:</span>
                      <span className="font-bold text-red-500">Locked</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Link to="/evaluate" data-testid="evaluate-link">
              <Card className="h-full hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer border-2 border-transparent hover:border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50">
                <CardHeader>
                  <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                    <Upload className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-xl">Practice Descriptive</CardTitle>
                  <CardDescription className="text-sm">
                    Upload handwritten answer for EMRS, KVS, NVS descriptive questions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      TGT & PGT level questions
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      CBSE pattern (4 marks)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Rank & Percentile
                    </li>
                  </ul>
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Remaining:</span>
                      <span className="font-bold text-blue-600">{getQuestionsRemaining()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}

          {(isFreeUser && !hasQuotaLeft) ? (
            <div className="relative" data-testid="mcq-link-locked">
              <Card className="h-full opacity-50 pointer-events-none border-2 border-gray-200 bg-gradient-to-br from-green-50 to-emerald-50">
                <CardHeader>
                  <div className="w-14 h-14 bg-gray-400 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                    <Lock className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-xl">Practice Objective</CardTitle>
                  <CardDescription className="text-sm">
                    {effectiveMcqLimit} MCQs per topic with instant scoring and analysis
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Access:</span>
                      <span className="font-bold text-red-500">Locked</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Link to="/mcq-practice" data-testid="mcq-link">
              <Card className="h-full hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer border-2 border-transparent hover:border-green-500 bg-gradient-to-br from-green-50 to-emerald-50">
                <CardHeader>
                  <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-xl">Practice Objective</CardTitle>
                  <CardDescription className="text-sm">
                    {effectiveMcqLimit} MCQs per topic with instant scoring and analysis
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      {effectiveMcqLimit} MCQs per topic
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      10 minute timer
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Key info & weak areas
                    </li>
                  </ul>
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Practice:</span>
                      <span className="font-bold text-green-600">Unlimited</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}

          <Link to="/notes" data-testid="notes-link">
            <Card className="h-full hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer border-2 border-transparent hover:border-amber-500 bg-gradient-to-br from-amber-50 to-yellow-50">
              <CardHeader>
                <div className="w-14 h-14 bg-amber-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <BookOpen className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl">Study Notes</CardTitle>
                <CardDescription className="text-sm">
                  Detailed CBSE based expert's notes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Comprehensive coverage
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Hindi & English
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Premium quality notes
                  </li>
                </ul>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">1st chapter:</span>
                    <span className="font-bold text-green-600">Free</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Notes Link - MOVED TO LAST */}
          <Link to="/notes" data-testid="notes-link">
            <Card className="h-full hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer border-2 border-transparent hover:border-green-500 bg-gradient-to-br from-green-50 to-emerald-50">
              <CardHeader>
                <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <BookOpen className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl">Study Notes</CardTitle>
                <CardDescription className="text-sm">
                  Get detailed, handwritten-style notes for every chapter
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Comprehensive coverage
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Hindi & English
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Premium quality notes
                  </li>
                </ul>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">1st chapter:</span>
                    <span className="font-bold text-green-600">Free</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

        </div>

        {/* Second row: NCERT Exemplar (TGT/PGT) + Question Bank + Merge PDFs + View History */}
        <div className={`grid ${(subjectSelection?.category === 'tgt' || subjectSelection?.category === 'pgt') ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-6 mb-12`}>

          {/* NCERT Exemplar - Only show for TGT and PGT */}
          {(subjectSelection?.category === 'tgt' || subjectSelection?.category === 'pgt') && (
            <div className="relative">
              {isFreeUser && (
                <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm rounded-xl z-10 flex items-center justify-center cursor-not-allowed">
                  <div className="bg-white rounded-xl p-6 text-center shadow-2xl max-w-sm mx-4">
                    <Lock className="w-12 h-12 text-orange-500 mx-auto mb-3" />
                    <h3 className="font-bold text-gray-800 mb-2">Premium Feature</h3>
                    <p className="text-sm text-gray-600 mb-3">Unlock NCERT Exemplar with Premium @ ₹499</p>
                    <Link to="/pro">
                      <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white">
                        <Crown className="w-4 h-4 mr-2" />
                        Upgrade Now
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
              <Link to={isFreeUser ? "#" : "/ncert-exemplar"} data-testid="ncert-exemplar-link" onClick={(e) => isFreeUser && e.preventDefault()}>
                <Card className={`h-full transition-all duration-300 border-2 bg-gradient-to-br from-orange-50 to-red-50 ${isFreeUser ? 'opacity-60' : 'hover:shadow-xl hover:scale-105 cursor-pointer border-transparent hover:border-orange-500'}`}>
                  <CardHeader>
                    <div className="w-14 h-14 bg-orange-600 rounded-xl flex items-center justify-center mb-4 shadow-lg relative">
                      <BookMarked className="w-7 h-7 text-white" />
                      {isFreeUser && <Lock className="w-5 h-5 text-white absolute -top-1 -right-1 bg-orange-700 rounded-full p-1" />}
                    </div>
                    <CardTitle className="text-xl flex items-center gap-2">
                      NCERT Exemplar
                      <Badge className="bg-orange-500 text-white text-xs">PREMIUM</Badge>
                    </CardTitle>
                    <CardDescription className="text-sm">
                      Practice NCERT Exemplar questions
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        {subjectSelection?.category === 'tgt' ? 'Class 6-10 Questions' : 'Class 9-12 Questions'}
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        Chapter-wise organized
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        Model answers included
                      </li>
                    </ul>
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Practice:</span>
                        <span className="font-bold text-orange-600">{isFreeUser ? 'Locked' : 'Unlimited'}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>
          )}
          {/* Question Bank — free users get 2 topics, paywall on 3rd */}
          <Link
            to={`/question-bank/${subjectSelection?.category || 'tgt'}/${subjectSelection?.subject || 'science'}`}
            data-testid="question-bank-link"
          >
            <Card className="h-full hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer border-2 border-transparent hover:border-purple-500 bg-gradient-to-br from-purple-50 to-indigo-50">
              <CardHeader>
                <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <Database className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl flex items-center gap-2">
                  Question Bank
                  <Badge className="bg-purple-600 text-white text-xs">BILINGUAL</Badge>
                </CardTitle>
                <CardDescription className="text-sm">
                  {subjectSelection?.category?.toUpperCase()} {subjectSelection?.subject?.replace('_',' ')} questions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600" />English / हिंदी bilingual</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600" />Topic-wise MCQs</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600" />Expert explanations</li>
                </ul>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Access:</span>
                    <span className="font-bold text-purple-600">{isFreeUser ? '2 topics free' : 'Unlimited'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Merge PDFs */}
          <Link to="/my-pdfs" data-testid="my-pdfs-link">
            <Card className="h-full hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer border-2 border-transparent hover:border-indigo-500 bg-gradient-to-br from-indigo-50 to-blue-50">
              <CardHeader>
                <div className="w-14 h-14 bg-indigo-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <FolderOpen className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl">Merge PDFs</CardTitle>
                <CardDescription className="text-sm">
                  Combine multiple topic notes into one PDF for easy printing
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Select topics & medium
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Auto-generate missing notes
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Download merged PDF
                  </li>
                </ul>
                <div className="mt-4 pt-4 border-t space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Max topics:</span>
                    <span className="font-bold text-indigo-600">20 per merge</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                    <Clock className="w-3 h-3" />
                    <span>Stored for 2 days only</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* History */}
          <Link to="/history" data-testid="history-link">
            <Card className="h-full hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer border-2 border-transparent hover:border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50">
              <CardHeader>
                <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <History className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl">View History</CardTitle>
                <CardDescription className="text-sm">
                  Review all your past submissions and feedback
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Past submissions
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Score tracking
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Performance insights
                  </li>
                </ul>
              </CardContent>
            </Card>
          </Link>
        </div>

        <Card className="bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <BookOpen className="w-6 h-6 text-blue-600" />
              <CardTitle>EMRS KVS NVS Descriptive Syllabus Coverage</CardTitle>
            </div>
            <CardDescription>Practice descriptive paper questions across all TGT & PGT subjects</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-purple-700 mb-2">PGT Syllabus (Post Graduate Teacher)</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {[
                  { name: "Hindi", path: "hindi" },
                  { name: "English", path: "english" },
                  { name: "Mathematics", path: "mathematics" },
                  { name: "Physics", path: "physics" },
                  { name: "Chemistry", path: "chemistry" },
                  { name: "Biology", path: "biology" },
                  { name: "Commerce", path: "commerce" },
                  { name: "Economics", path: "economics" },
                  { name: "Geography", path: "geography" },
                  { name: "History", path: "history" },
                  { name: "Computer Science", path: "computer_science" },
                  { name: "Music", path: "music" },
                  { name: "Art", path: "art" }
                ].map((subject) => (
                  <Link key={subject.path} to={`/syllabus/pgt/${subject.path}`}>
                    <div className="px-3 py-2 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg text-center font-medium text-gray-700 border border-purple-100 hover:shadow-md hover:border-purple-300 transition-all cursor-pointer text-sm">
                      {subject.name}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-blue-700 mb-2">TGT Syllabus (Trained Graduate Teacher)</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {[
                  { name: "Hindi", path: "hindi" },
                  { name: "English", path: "english" },
                  { name: "Mathematics", path: "mathematics" },
                  { name: "Science", path: "science" },
                  { name: "Social Science", path: "social_science" },
                  { name: "Computer Science", path: "computer_science" },
                  { name: "Physical Education", path: "physical_education" },
                  { name: "Music", path: "music" },
                  { name: "Art", path: "art" }
                ].map((subject) => (
                  <Link key={subject.path} to={`/syllabus/tgt/${subject.path}`}>
                    <div className="px-3 py-2 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg text-center font-medium text-gray-700 border border-blue-100 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer text-sm">
                      {subject.name}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-green-700 mb-2">Non-Teaching</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {[
                  { name: "Hostel Warden", path: "hostel_warden" },
                  { name: "Library Science", path: "library_science" }
                ].map((subject) => (
                  <Link key={subject.path} to={`/syllabus/non_teaching/${subject.path}`}>
                    <div className="px-3 py-2 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg text-center font-medium text-gray-700 border border-green-100 hover:shadow-md hover:border-green-300 transition-all cursor-pointer text-sm">
                      {subject.name}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* SEO Footer Section */}
        <div className="mt-12 p-6 bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200">
          <h2 className="text-xl font-bold text-gray-800 mb-4">About EMRS KVS NVS Descriptive Paper Practice</h2>
          <div className="grid md:grid-cols-2 gap-6 text-sm text-gray-600">
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">EMRS Descriptive Paper Pattern</h3>
              <p>EMRS (Eklavya Model Residential Schools) Tier-II exam includes 15 descriptive questions worth 60 marks. Practice with our CBSE Expert Team feedback system covering complete EMRS descriptive syllabus for TGT and PGT posts.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">KVS NVS Descriptive Syllabus</h3>
              <p>KVS (Kendriya Vidyalaya Sangathan) and NVS (Navodaya Vidyalaya Samiti) descriptive papers test subject knowledge and pedagogy. Our platform covers complete KVS NVS descriptive syllabus with CBSE pattern scoring.</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">EMRS Descriptive Paper</span>
            <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">KVS NVS Descriptive Paper</span>
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs">TGT PGT Practice</span>
            <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">Descriptive Syllabus 2025</span>
          </div>
        </div>
        
        <footer className="mt-8 text-center text-sm text-gray-500">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
            <Link to="/terms" className="hover:text-blue-600 hover:underline">Terms</Link>
            <span className="text-gray-300">|</span>
            <Link to="/refund-policy" className="hover:text-blue-600 hover:underline">Refund Policy</Link>
            <span className="text-gray-300">|</span>
            <Link to="/privacy-policy" className="hover:text-blue-600 hover:underline">Privacy Policy</Link>
            <span className="text-gray-300">|</span>
            <Link to="/support" className="hover:text-blue-600 hover:underline" data-testid="ints-quick-btn">INTS</Link>
          </div>
          <div className="mt-2 text-gray-400">Best platform for EMRS, KVS, NVS descriptive paper preparation</div>
        </footer>
      </main>
    </div>
  );
}